import React, { useState, useEffect } from "react";
import { X, Save, Loader2 } from "lucide-react";
import { Button } from "./Button";
import { Input } from "./input";
import CountryDropdown from "./CountryDropdown";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import { 
    createContactInfo, 
    updateContactInfo, 
    clearContactInfoError 
} from "../../redux/Slices/contactInfoSlice";
import { ContactInfoBackendResponse, ContactInfoCreateDto } from "../../modules/shared/model/contact-info.interfaces";

interface Country {
    id: string;
    name: string;
    code: string;
}

interface ContactInfoModalProps {
    isOpen: boolean;
    onClose: () => void;
    contactInfo?: ContactInfoBackendResponse | null;
    onSuccess?: () => void;
}

const ContactInfoModal: React.FC<ContactInfoModalProps> = ({
    isOpen,
    onClose,
    contactInfo,
    onSuccess,
}) => {
    const dispatch = useAppDispatch();
    const { loading, error } = useAppSelector((state: any) => state.contactInfos);

    const [formData, setFormData] = useState<ContactInfoCreateDto>({
        name: "",
        email: "",
        phone: "",
        whatsapp: "",
        address: "",
        city: "",
        state: "",
        postalCode: undefined,
        weekdayHours: "",
        weekendHours: "",
        vatNumber: "",
        country: undefined,
    });

    const [selectedCountry, setSelectedCountry] = useState<Country | null>(null);

    // Initialize form data when modal opens or contactInfo changes
    useEffect(() => {
        if (isOpen) {
            if (contactInfo) {
                setFormData({
                    name: contactInfo.name || "",
                    email: contactInfo.email || "",
                    phone: contactInfo.phone || "",
                    whatsapp: contactInfo.whatsapp || "",
                    address: contactInfo.address || "",
                    city: contactInfo.city || "",
                    state: contactInfo.state || "",
                    postalCode: contactInfo.postalCode || undefined,
                    weekdayHours: contactInfo.weekdayHours || "",
                    weekendHours: contactInfo.weekendHours || "",
                    vatNumber: contactInfo.vatNumber || "",
                    country: contactInfo.country ? { id: contactInfo.country.id } : undefined,
                });
                setSelectedCountry(contactInfo.country || null);
            } else {
                // Reset form for new contact info
                setFormData({
                    name: "",
                    email: "",
                    phone: "",
                    whatsapp: "",
                    address: "",
                    city: "",
                    state: "",
                    postalCode: undefined,
                    weekdayHours: "",
                    weekendHours: "",
                    vatNumber: "",
                    country: undefined,
                });
                setSelectedCountry(null);
            }
            dispatch(clearContactInfoError());
        }
    }, [isOpen, contactInfo, dispatch]);

    const handleInputChange = (field: keyof ContactInfoCreateDto, value: string | number | undefined) => {
        setFormData(prev => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleCountrySelect = (country: Country | null) => {
        setSelectedCountry(country);
        setFormData(prev => ({
            ...prev,
            country: country ? { id: country.id } : undefined,
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        try {
            if (contactInfo) {
                // Update existing contact info
                await dispatch(updateContactInfo({ 
                    id: contactInfo.id, 
                    data: formData 
                })).unwrap();
            } else {
                // Create new contact info
                await dispatch(createContactInfo(formData)).unwrap();
            }
            
            onSuccess?.();
            onClose();
        } catch (error) {
            console.error("Failed to save contact info:", error);
        }
    };

    const handleClose = () => {
        if (!loading) {
            onClose();
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-2 sm:p-4">
            <div className="bg-white rounded-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
                <div className="p-4 sm:p-6 border-b">
                    <div className="flex items-center justify-between">
                        <h2 className="text-lg sm:text-xl font-semibold">
                            {contactInfo ? "Edit Contact Information" : "Add Contact Information"}
                        </h2>
                        <button 
                            onClick={handleClose} 
                            disabled={loading}
                            className="p-2 hover:bg-gray-100 rounded-full disabled:opacity-50"
                        >
                            <X className="h-4 w-4 sm:h-5 sm:w-5" />
                        </button>
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-4">
                    {error && (
                        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                            {error}
                        </div>
                    )}

                    {/* Basic Information */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Company/Contact Name *
                            </label>
                            <Input
                                type="text"
                                value={formData.name || ""}
                                onChange={(e) => handleInputChange("name", e.target.value)}
                                placeholder="Enter company or contact name"
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                VAT Number
                            </label>
                            <Input
                                type="text"
                                value={formData.vatNumber || ""}
                                onChange={(e) => handleInputChange("vatNumber", e.target.value)}
                                placeholder="Enter VAT number"
                            />
                        </div>
                    </div>

                    {/* Contact Details */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Email *
                            </label>
                            <Input
                                type="email"
                                value={formData.email || ""}
                                onChange={(e) => handleInputChange("email", e.target.value)}
                                placeholder="Enter email address"
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Phone
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <span className="text-gray-500 text-sm">+</span>
                                </div>
                                <Input
                                    type="tel"
                                    value={formData.phone?.replace(/^\+/, "") || ""}
                                    onChange={(e) => handleInputChange("phone", `+${e.target.value}`)}
                                    placeholder="1 (555) 123-4567"
                                    className="pl-8"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                WhatsApp
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <span className="text-gray-500 text-sm">+</span>
                                </div>
                                <Input
                                    type="tel"
                                    value={formData.whatsapp?.replace(/^\+/, "") || ""}
                                    onChange={(e) => handleInputChange("whatsapp", `+${e.target.value}`)}
                                    placeholder="1 (555) 123-4567"
                                    className="pl-8"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Country Selection */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Country *
                        </label>
                        <CountryDropdown
                            selectedCountry={selectedCountry}
                            onCountrySelect={handleCountrySelect}
                            placeholder="Select a country"
                            disabled={loading}
                        />
                    </div>

                    {/* Address */}
                    <div>
                        <h3 className="text-base font-medium mb-3">Address</h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Street Address
                                </label>
                                <Input
                                    type="text"
                                    value={formData.address || ""}
                                    onChange={(e) => handleInputChange("address", e.target.value)}
                                    placeholder="Enter street address"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    City
                                </label>
                                <Input
                                    type="text"
                                    value={formData.city || ""}
                                    onChange={(e) => handleInputChange("city", e.target.value)}
                                    placeholder="Enter city"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    State/Region
                                </label>
                                <Input
                                    type="text"
                                    value={formData.state || ""}
                                    onChange={(e) => handleInputChange("state", e.target.value)}
                                    placeholder="Enter state or region"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Postal Code
                                </label>
                                <Input
                                    type="text"
                                    value={formData.postalCode || ""}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        if (value === "") {
                                            handleInputChange("postalCode", undefined);
                                        } else {
                                            handleInputChange("postalCode", value);
                                        }
                                    }}
                                    placeholder="Enter postal code"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Business Hours */}
                    <div>
                        <h3 className="text-base font-medium mb-3">Business Hours</h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Weekday Hours
                                </label>
                                <Input
                                    type="text"
                                    value={formData.weekdayHours || ""}
                                    onChange={(e) => handleInputChange("weekdayHours", e.target.value)}
                                    placeholder="e.g., 9:00 AM - 5:00 PM"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Weekend Hours
                                </label>
                                <Input
                                    type="text"
                                    value={formData.weekendHours || ""}
                                    onChange={(e) => handleInputChange("weekendHours", e.target.value)}
                                    placeholder="e.g., 10:00 AM - 2:00 PM"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="pt-4 border-t flex justify-end gap-3">
                        <Button 
                            type="button" 
                            variant="outline" 
                            onClick={handleClose}
                            disabled={loading}
                        >
                            Cancel
                        </Button>
                        <Button 
                            type="submit" 
                            disabled={loading || !formData.name || !formData.email || !selectedCountry}
                            className="flex items-center gap-2"
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                    Saving...
                                </>
                            ) : (
                                <>
                                    <Save className="h-4 w-4" />
                                    {contactInfo ? "Update" : "Create"}
                                </>
                            )}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ContactInfoModal;
